import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RangeValue } from 'rc-picker/lib/interface';
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { Button, DatePicker, Input, Table } from 'antd';
import moment, { Moment } from 'moment';
import queryString from 'query-string';
import pluralize from 'pluralize';
import cx from 'classnames';
import { useDidUpdate } from 'rooks';
import { isEmpty } from 'lodash';

import {
  closureCounter,
  defaultFilterValues,
  FinancialReportFilterFieldEnum,
  FinancialReportFilters,
  FinancialReportFilterTypes,
  FinancialReportMultipleFilterValues,
  getFormattedValuesForFilterMultiple,
  mapFinancialReportFiltersToRequestModel,
} from './utils';
import {
  getFinancialReportDetails,
  getFinancialReportExcel,
  getValuesForFinancialReportFilters,
} from 'redux/reducers/homeDashboardSlice';
import {
  FinancialReportDetailsResponseModel,
  FinancialResponseModel,
  FinancialReportSortFieldEnum,
  FinancialFilterViewModel,
} from 'api/backofficeApiClient';
import { SortingDirectionEnum } from 'enums';
import { getDateWithoutTime } from 'utils/dateUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import { getSortOrderRequestValue } from 'utils/sortUtils';
import { getFinancialReportColumns } from './columnUtils';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import { typeConversionToStringArray } from 'utils/convertUtils';
import LastSyncDateSection from 'components/LastSyncDateSection';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import useSortColumns, { ISortParams } from 'hooks/useSortColumns';
import { useAppDispatch } from 'hooks/useAppDispatch';
import ProfitReportSettingsModal, {
  mapColumnSettingsToBooleanValues,
} from 'components/modals/ProfitReportSettingsModal';
import { pageDefaultSizeTable } from 'constants/index';
import { isShowFooterTableOnLastPage } from 'utils/tableSettings';

import commonStyles from '../UserHomePage.module.less';
import styles from './ProfitReportDetailsPage.module.less';

export const defaultSortParams: ISortParams<FinancialReportSortFieldEnum> = {
  sortOrder: 'descend',
  sortBy: FinancialReportSortFieldEnum.Date,
};

export default function ProfitReportDetailsPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [pageNavigationHistory, setPageNavigationHistory] = useState<number[]>(
    [],
  );
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [isFinancialReportDataLoading, setIsFinancialReportDataLoading] =
    useState(false);
  const [financialReportData, setFinancialReportData] =
    useState<FinancialResponseModel>();
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const {
    profitStartDate: startDate,
    profitEndDate: endDate,
    sortBy,
    sortOrder,
    query,
    customerName,
    member1Name,
    member2Name,
    workerTypeOrWorkersId,
    parentVendor,
    workerName,
    workerJobTitle,
    commissionDetails,
    memberMarkup,
    billHoursReg,
    billHoursOT,
    billRateReg,
    billRateOT,
    billAmount,
    payHoursReg,
    payHoursOT,
    payRateReg,
    payRateOT,
    payAmount,
    MBPRate,
    MBPFee,
    MSPFee,
    VMSFee,
    totalProfit,
    member1USDHr,
    member2USDHr,
    member1ProfitPercent,
    member2ProfitPercent,
    member1Profit,
    member2Profit,
    invoiceNumber,
  } = queryString.parse(location.search);
  const [isTableSettings, setIsTableSettings] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>(query);
  const initialSortParams: ISortParams<FinancialReportSortFieldEnum> =
    sortBy && sortOrder
      ? {
          sortBy: parseInt(sortBy),
          sortOrder,
        }
      : defaultSortParams;
  const initialFilters: FinancialReportFilters = {
    [FinancialReportFilterTypes.Date]:
      startDate && endDate
        ? { startDate: moment(startDate), endDate: moment(endDate) }
        : undefined,
    [FinancialReportFilterTypes.Columns]: {
      [FinancialReportSortFieldEnum.CustomerName]:
        typeConversionToStringArray(customerName),
      [FinancialReportSortFieldEnum.Member1Name]:
        typeConversionToStringArray(member1Name),
      [FinancialReportSortFieldEnum.Member2Name]:
        typeConversionToStringArray(member2Name),
      [FinancialReportSortFieldEnum.WorkerTypeWorkerID]:
        typeConversionToStringArray(workerTypeOrWorkersId),
      [FinancialReportSortFieldEnum.ParentVendor]:
        typeConversionToStringArray(parentVendor),
      [FinancialReportSortFieldEnum.WorkerName]:
        typeConversionToStringArray(workerName),
      [FinancialReportSortFieldEnum.WorkerJobTitle]:
        typeConversionToStringArray(workerJobTitle),
      [FinancialReportSortFieldEnum.CommissionDetails]:
        typeConversionToStringArray(commissionDetails),
      [FinancialReportSortFieldEnum.MemberMarkup]:
        typeConversionToStringArray(memberMarkup),
      [FinancialReportSortFieldEnum.BillHoursReg]:
        typeConversionToStringArray(billHoursReg),
      [FinancialReportSortFieldEnum.BillHoursOT]:
        typeConversionToStringArray(billHoursOT),
      [FinancialReportSortFieldEnum.BillRateReg]:
        typeConversionToStringArray(billRateReg),
      [FinancialReportSortFieldEnum.BillRateOT]:
        typeConversionToStringArray(billRateOT),
      [FinancialReportSortFieldEnum.BillAmount]:
        typeConversionToStringArray(billAmount),
      [FinancialReportSortFieldEnum.PayHoursReg]:
        typeConversionToStringArray(payHoursReg),
      [FinancialReportSortFieldEnum.PayHoursOT]:
        typeConversionToStringArray(payHoursOT),
      [FinancialReportSortFieldEnum.PayRateReg]:
        typeConversionToStringArray(payRateReg),
      [FinancialReportSortFieldEnum.PayRateOT]:
        typeConversionToStringArray(payRateOT),
      [FinancialReportSortFieldEnum.PayAmount]:
        typeConversionToStringArray(payAmount),
      [FinancialReportSortFieldEnum.MBPRate]:
        typeConversionToStringArray(MBPRate),
      [FinancialReportSortFieldEnum.MBPFee]:
        typeConversionToStringArray(MBPFee),
      [FinancialReportSortFieldEnum.MSPFee]:
        typeConversionToStringArray(MSPFee),
      [FinancialReportSortFieldEnum.VMSFee]:
        typeConversionToStringArray(VMSFee),
      [FinancialReportSortFieldEnum.TotalProfit]:
        typeConversionToStringArray(totalProfit),
      [FinancialReportSortFieldEnum.Member1USDHr]:
        typeConversionToStringArray(member1USDHr),
      [FinancialReportSortFieldEnum.Member2USDHr]:
        typeConversionToStringArray(member2USDHr),
      [FinancialReportSortFieldEnum.Member1ProfitPercent]:
        typeConversionToStringArray(member1ProfitPercent),
      [FinancialReportSortFieldEnum.Member2ProfitPercent]:
        typeConversionToStringArray(member2ProfitPercent),
      [FinancialReportSortFieldEnum.Member1Profit]:
        typeConversionToStringArray(member1Profit),
      [FinancialReportSortFieldEnum.Member2Profit]:
        typeConversionToStringArray(member2Profit),
      [FinancialReportSortFieldEnum.InvoiceNumber]:
        typeConversionToStringArray(invoiceNumber),
    },
  };
  const [filters, setFilters] =
    useState<FinancialReportFilters>(initialFilters);
  const [filtersData, setFiltersData] =
    useState<FinancialReportMultipleFilterValues>();
  const [isFiltersDataLoaded, setIsFiltersDataLoaded] =
    useState<boolean>(false);

  const isActiveFilters = useMemo(() => {
    const activeFilters = Object.values(
      filters[FinancialReportFilterTypes.Columns],
    ).find((filterValue) => filterValue?.length);

    return (
      filters[FinancialReportFilterTypes.Date] || activeFilters || searchQuery
    );
  }, [filters, searchQuery]);

  const fincancialReportCountMessage = pluralize(
    'record',
    financialReportData?.totalCount,
    true,
  );

  const handleUpdateFilters = useCallback(
    (type: FinancialReportSortFieldEnum, newFilters: string[]) => {
      setFilters({
        ...filters,
        columns: { ...filters.columns, [type]: newFilters },
      });

      setPageNavigationHistory([]);
      setCurrentTablePage(1);
    },
    [filters],
  );

  const { sortColumns, sortParams, setSortParams } = useSortColumns(
    useMemo(
      () =>
        getFinancialReportColumns({
          filters,
          onChange: handleUpdateFilters,
          isFiltersDataLoaded,
          filtersData,
          searchQuery,
          columnsSettings: financialReportData?.filter,
        }),
      [
        filters,
        isFiltersDataLoaded,
        filtersData,
        handleUpdateFilters,
        searchQuery,
        financialReportData,
      ],
    ),
    initialSortParams,
  );

  useEffect(() => {
    loadValuesForFinancialReportFilters();
    // eslint-disable-next-line
  }, []);

  useDidUpdate(() => {
    const queryData = {
      ...sortParams,
      query: searchQuery || undefined,
      profitStartDate: filters[FinancialReportFilterTypes.Date]?.startDate
        ? filters[FinancialReportFilterTypes.Date]?.startDate?.format(
            'MM/DD/YYYY',
          )
        : undefined,
      profitEndDate: filters[FinancialReportFilterTypes.Date]?.endDate
        ? filters[FinancialReportFilterTypes.Date]?.endDate?.format(
            'MM/DD/YYYY',
          )
        : undefined,
      customerName: filters.columns[FinancialReportSortFieldEnum.CustomerName],
      member1Name: filters.columns[FinancialReportSortFieldEnum.Member1Name],
      member2Name: filters.columns[FinancialReportSortFieldEnum.Member2Name],
      parentVendor: filters.columns[FinancialReportSortFieldEnum.ParentVendor],
      workerTypeOrWorkersId:
        filters.columns[FinancialReportSortFieldEnum.WorkerTypeWorkerID],
      workerName: filters.columns[FinancialReportSortFieldEnum.WorkerName],
      commissionDetails:
        filters.columns[FinancialReportSortFieldEnum.CommissionDetails],
      memberMarkup: filters.columns[FinancialReportSortFieldEnum.MemberMarkup],
      billHoursReg: filters.columns[FinancialReportSortFieldEnum.BillHoursReg],
      billHoursOT: filters.columns[FinancialReportSortFieldEnum.BillHoursOT],
      billRateReg: filters.columns[FinancialReportSortFieldEnum.BillRateReg],
      billRateOT: filters.columns[FinancialReportSortFieldEnum.BillRateOT],
      billAmount: filters.columns[FinancialReportSortFieldEnum.BillAmount],
      payHoursReg: filters.columns[FinancialReportSortFieldEnum.PayHoursReg],
      payHoursOT: filters.columns[FinancialReportSortFieldEnum.PayHoursOT],
      payRateReg: filters.columns[FinancialReportSortFieldEnum.PayRateReg],
      payRateOT: filters.columns[FinancialReportSortFieldEnum.PayRateOT],
      payAmount: filters.columns[FinancialReportSortFieldEnum.PayAmount],
      MBPRate: filters.columns[FinancialReportSortFieldEnum.MBPRate],
      MBPFee: filters.columns[FinancialReportSortFieldEnum.MBPFee],
      MSPFee: filters.columns[FinancialReportSortFieldEnum.MSPFee],
      VMSFee: filters.columns[FinancialReportSortFieldEnum.VMSFee],
      totalProfit: filters.columns[FinancialReportSortFieldEnum.TotalProfit],
      member1USDHr: filters.columns[FinancialReportSortFieldEnum.Member1USDHr],
      member2USDHr: filters.columns[FinancialReportSortFieldEnum.Member2USDHr],
      member1ProfitPercent:
        filters.columns[FinancialReportSortFieldEnum.Member1ProfitPercent],
      member2ProfitPercent:
        filters.columns[FinancialReportSortFieldEnum.Member2ProfitPercent],
      member1Profit:
        filters.columns[FinancialReportSortFieldEnum.Member1Profit],
      member2Profit:
        filters.columns[FinancialReportSortFieldEnum.Member2Profit],
      invoiceNumber:
        filters.columns[FinancialReportSortFieldEnum.InvoiceNumber],
    };

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [filters, sortParams.sortBy, sortParams.sortOrder, searchQuery]);

  const loadValuesForFinancialReportFilters = async () => {
    const result = await dispatch(getValuesForFinancialReportFilters());

    if (getValuesForFinancialReportFilters.fulfilled.match(result)) {
      setFiltersData(getFormattedValuesForFilterMultiple(result.payload));
    }
    setIsFiltersDataLoaded(true);
  };

  const loadFinancialReportData = useCallback(async () => {
    setIsFinancialReportDataLoading(true);

    const fieldsFilter = mapFinancialReportFiltersToRequestModel(
      filters.columns,
    );

    const result = await dispatch(
      getFinancialReportDetails({
        pageNumber: currentTablePage - 1,
        startDate: filters[FinancialReportFilterTypes.Date]?.startDate
          ? getDateWithoutTime(
              filters[FinancialReportFilterTypes.Date]?.startDate,
            )
          : undefined,
        endDate: filters[FinancialReportFilterTypes.Date]?.endDate
          ? getDateWithoutTime(
              filters[FinancialReportFilterTypes.Date]?.endDate,
            )
          : undefined,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        fieldsFilter,
      }),
    );

    if (getFinancialReportDetails.fulfilled.match(result)) {
      setFinancialReportData(result.payload);
    } else {
      setFinancialReportData({
        totalCount: 0,
        details: [],
      });
    }

    setIsFinancialReportDataLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters,
    dispatch,
    currentTablePage,
    sortParams.sortBy,
    sortParams.sortOrder,
  ]);

  const loadInitialData = useCallback(async () => {
    await loadFinancialReportData();

    setIsInitialDataLoaded(true);
  }, [loadFinancialReportData]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleDownloadReport = async () => {
    const fieldsFilter = mapFinancialReportFiltersToRequestModel(
      filters.columns,
    );

    const result = await dispatch(
      getFinancialReportExcel({
        startDate: filters[FinancialReportFilterTypes.Date]?.startDate
          ? getDateWithoutTime(
              filters[FinancialReportFilterTypes.Date]?.startDate,
            )
          : undefined,
        endDate: filters[FinancialReportFilterTypes.Date]?.endDate
          ? getDateWithoutTime(
              filters[FinancialReportFilterTypes.Date]?.endDate,
            )
          : undefined,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        fieldsFilter,
      }),
    );

    if (getFinancialReportExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleSelectDate =
    (type: FinancialReportFilterTypes) => (date: RangeValue<Moment>) => {
      if (date) {
        setFilters({
          ...filters,
          [type]: {
            startDate: date[0],
            endDate: date[1],
          },
        });
      } else {
        setFilters({
          ...filters,
          [type]: undefined,
        });
      }
      setCurrentTablePage(1);
    };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filter: Record<string, FilterValue | null>,
    sort:
      | SorterResult<FinancialReportDetailsResponseModel>
      | SorterResult<FinancialReportDetailsResponseModel>[],
  ) => {
    const sortOrder =
      (sort as SorterResult<FinancialReportDetailsResponseModel>).order ||
      SortingDirectionEnum.Descend;
    const sortBy =
      ((sort as SorterResult<FinancialReportDetailsResponseModel>).column
        ?.key as FinancialReportSortFieldEnum) ||
      FinancialReportSortFieldEnum.Date;

    setSortParams(({ sortBy: prevSortBy, sortOrder: prevSortOrder }) => {
      if (prevSortBy !== sortBy || prevSortOrder !== sortOrder) {
        if (pagination.current !== 1) {
          // Add new page number to history if sorting has changed
          // and user should be redirected to the first page
          setPageNavigationHistory([
            ...pageNavigationHistory,
            pagination.current || 1,
          ]);
          // Reset table page number if sorting has changed
          setCurrentTablePage(1);
        }
      } else {
        // Add current page number to history
        setPageNavigationHistory([...pageNavigationHistory, currentTablePage]);
        setCurrentTablePage(pagination.current || 1);
      }

      return {
        sortOrder,
        sortBy,
      };
    });
  };

  const handleSearchQueryChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const {
      target: { value },
    } = event;

    setSearchQuery(value);
  };

  const handleNavigationHistoryPush = () => {
    if (!pageNavigationHistory.length) return;

    const currentPage = pageNavigationHistory[pageNavigationHistory.length - 1];
    const duplicateNavigationHistory = [...pageNavigationHistory];

    duplicateNavigationHistory.pop();
    setPageNavigationHistory(duplicateNavigationHistory);
    setCurrentTablePage(currentPage);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setSortParams(defaultSortParams);
    setFilters(defaultFilterValues);
  };

  const handleOpenTableSettings = () => {
    setIsTableSettings(true);
  };

  const handleCloseTableSettings = () => {
    setIsTableSettings(false);
  };

  const handleSuccess = async (data: FinancialFilterViewModel) => {
    const columnsSettings: { [key: number]: boolean | undefined } =
      mapColumnSettingsToBooleanValues(data);
    let isNeedResetFilters = false;

    const updatedFilters = Object.entries(
      filters[FinancialReportFilterTypes.Columns],
    ).map((columnValues) => {
      const index = +columnValues[0];
      const isVisible = columnsSettings[index];
      const hasFilters =
        filters[FinancialReportFilterTypes.Columns][
          index as FinancialReportFilterFieldEnum
        ]?.length > 0;

      if (!isVisible && hasFilters) {
        isNeedResetFilters = true;
      }
      return isVisible ? columnValues : [+index, []];
    });

    if (isNeedResetFilters) {
      setFilters({
        ...filters,
        [FinancialReportFilterTypes.Columns]:
          Object.fromEntries(updatedFilters),
      });
    } else {
      setFinancialReportData({
        ...financialReportData,
        filter: data,
      });
    }

    if (!columnsSettings[sortBy]) {
      setSortParams(defaultSortParams);
    }

    handleCloseTableSettings();
  };

  return (
    <>
      {isTableSettings && (
        <ProfitReportSettingsModal
          onClose={handleCloseTableSettings}
          onSuccess={handleSuccess}
        />
      )}
      <div className="page-wrapper scroll-box">
        <div className="page-holder">
          <BasePageLabel label="Dashboard details">
            {financialReportData?.lastSyncDate && (
              <div className="title-line__info">
                <LastSyncDateSection
                  syncDate={financialReportData.lastSyncDate}
                  title="Profit report is automatically refreshed every 15 minutes."
                />
              </div>
            )}
          </BasePageLabel>

          <div className="page-box page-inner">
            <div className="page-inner-container">
              <BaseTableHeader
                title="Profit Report"
                enableSubTitle={isInitialDataLoaded}
                subTitle={fincancialReportCountMessage}
                classes={{
                  wrapper: 'align-start',
                }}
              >
                <div>
                  <Button onClick={handleOpenTableSettings}>Customize</Button>
                </div>
                {isActiveFilters && (
                  <div>
                    <Button onClick={handleResetFilters}>
                      Clear all filters
                    </Button>
                  </div>
                )}
                <div className={styles.searchBox}>
                  <Input
                    prefix={<span className="icon icon-search" />}
                    value={searchQuery}
                    onChange={handleSearchQueryChange}
                    placeholder="Start typing to highlight matching information"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <DatePicker.RangePicker
                    key="filterByDate"
                    format="MM/DD/YYYY"
                    separator="-"
                    className="d-flex"
                    value={
                      filters[FinancialReportFilterTypes.Date]
                        ? [
                            moment(
                              filters[FinancialReportFilterTypes.Date]
                                ?.startDate,
                            ),
                            moment(
                              filters[FinancialReportFilterTypes.Date]?.endDate,
                            ),
                          ]
                        : undefined
                    }
                    onChange={handleSelectDate(FinancialReportFilterTypes.Date)}
                  />
                </div>

                <div>
                  <Button
                    className={commonStyles.saveBtn}
                    onClick={handleDownloadReport}
                    icon={<span className="icon icon-xls-file" />}
                  />
                </div>
              </BaseTableHeader>
              {!isEmpty(sortColumns) && (
                <Table
                  className={cx(styles.table, 'custom-table')}
                  pagination={{
                    total: financialReportData?.totalCount,
                    pageSize: pageDefaultSizeTable,
                    current: currentTablePage,
                    showSizeChanger: false,
                    showTotal: () =>
                      !!pageNavigationHistory.length && (
                        <Button
                          onClick={handleNavigationHistoryPush}
                          type="link"
                          size="small"
                          className="with-icon"
                          icon={<i className="icon icon-arrow-left"></i>}
                        >
                          Previous page (
                          {
                            pageNavigationHistory[
                              pageNavigationHistory.length - 1
                            ]
                          }
                          )
                        </Button>
                      ),
                  }}
                  summary={() => {
                    if (
                      !isInitialDataLoaded ||
                      !isShowFooterTableOnLastPage(
                        financialReportData?.totalCount,
                        pageDefaultSizeTable,
                        financialReportData?.pageNumber,
                      )
                    )
                      return;
                    // https://mybasepay.atlassian.net/browse/MBO-2766
                    const getCorrectIndex = closureCounter();

                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={getCorrectIndex()}>
                          Total
                        </Table.Summary.Cell>
                        {financialReportData?.filter?.isMember1Name && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isMember2Name && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter
                          ?.isWorkerTypeOrWorkerId && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isParentVendor && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isWorkerName && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isWorkerJobTitle && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isCommissionDetails && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isDate && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isMemberMarkup && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isBillHoursReg && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="bill-hours-reg-total">
                              {financialReportData?.grandTotal?.billHoursReg}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isBillHoursOt && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="bill-hours-ot-total">
                              {financialReportData?.grandTotal?.billHoursOT}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isBillRateReg && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isBillRateOt && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isBillAmount && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="bill-amount-total">
                              {financialReportData?.grandTotal?.billAmount}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isPayHoursReg && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="pay-hours-reg-total">
                              {financialReportData?.grandTotal?.payHoursReg}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isPayHoursOt && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="pay-hours-ot-total">
                              {financialReportData?.grandTotal?.payHoursOT}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isPayRateReg && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isPayRateOt && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isPayAmount && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="pay-amount-total">
                              {financialReportData?.grandTotal?.payAmount}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isMBPRate && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isMBPFee && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="mbp-fee-total">
                              {financialReportData?.grandTotal?.mbpFee}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isMSPFee && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="msp-fee-total">
                              {financialReportData?.grandTotal?.mspFee}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isVMSFee && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="vms-fee-total">
                              {financialReportData?.grandTotal?.vmsFee}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isTotalProfit && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="total-profit">
                              {financialReportData?.grandTotal?.totalProfit}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isMember1USDHr && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isMember2USDHr && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter
                          ?.isMember1ProfitPercent && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter
                          ?.isMember2ProfitPercent && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                        {financialReportData?.filter?.isMember1Profit && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="member1-profit-total">
                              {financialReportData?.grandTotal?.member1Profit}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isMember2Profit && (
                          <Table.Summary.Cell index={getCorrectIndex()}>
                            <span data-id="member2-profit-total">
                              {financialReportData?.grandTotal?.member2Profit}
                            </span>
                          </Table.Summary.Cell>
                        )}
                        {financialReportData?.filter?.isInvoiceNumber && (
                          <Table.Summary.Cell index={getCorrectIndex()} />
                        )}
                      </Table.Summary.Row>
                    );
                  }}
                  showSorterTooltip={false}
                  onChange={handleTableChange}
                  columns={sortColumns}
                  dataSource={financialReportData?.details}
                  rowKey={(record) =>
                    (record?.netSuiteInvoiceId as number).toString()
                  }
                  loading={isFinancialReportDataLoading}
                  scroll={{ x: '100%' }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
